import { BusinessType } from "./businessType";
import { DATE_FORMATS, DEFAULT_DATE_FORMAT, isHttpUrl } from "./bookingLink";

export type ReservationMode = "link_out" | "enquiry";

export const RESERVATION_FIELDS = ["date", "time", "party_size", "name", "email", "phone", "notes"] as const;
export type ReservationField = (typeof RESERVATION_FIELDS)[number];
const CONTACT_FIELDS: ReservationField[] = ["email", "phone"];
const OPTIONAL_FIELDS: ReservationField[] = ["notes"];
/** Arguments of the chatbot's reservation tools, so not usable as a custom field id. */
const RESERVED_FIELD_IDS = ["hotel_id", "session_id", "conversation_id", "reservation_type"];
export const MAX_CUSTOM_FIELDS = 5;
export const MAX_FIELD_LABEL = 50;

export const RESERVATION_FIELD_LABELS: Record<ReservationField, string> = {
  date: "Date",
  time: "Time",
  party_size: "Party size",
  name: "Name",
  email: "Email",
  phone: "Phone",
  notes: "Notes",
};

export const RESERVATION_MODE_OPTIONS: { value: ReservationMode; label: string }[] = [
  { value: "link_out", label: "Send guest to a booking link" },
  { value: "enquiry", label: "Send request by email" },
];

/** A detail the chatbot asks for: a built-in field, or a custom one with its own label. */
export interface ReservationFieldSetting {
  id: string;
  label?: string;
  type?: "text";
  required: boolean;
  /** An extra detail the admin added, as opposed to a built-in field. */
  custom?: boolean;
}

/** A non-room reservation type, as the chatbot reads it from ``property_details.reservation_types``. */
export interface ReservationType {
  id: string;
  label: string;
  mode: ReservationMode;
  url_template?: string;
  date_format?: string;
  fields: ReservationFieldSetting[];
  enquiry_email?: string;
}

/** A field as saved: a built-in id when it keeps its default required flag, else an object. */
export type SavedReservationField = string | { id: string; required: boolean } | { id: string; label: string; type: "text"; required: boolean };

export type ReservationTypeErrors = Partial<
  Record<"id" | "label" | "url_template" | "enquiry_email" | "fields" | "custom_fields", string>
>;

const SLUG_RE = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const isBuiltinField = (value: unknown): value is ReservationField =>
  (RESERVATION_FIELDS as readonly string[]).includes(value as string);

export const builtinField = (id: ReservationField, required = !OPTIONAL_FIELDS.includes(id)): ReservationFieldSetting => ({
  id,
  required,
});

export const customFields = (type: ReservationType) => type.fields.filter((field) => field.custom);

/** Lowercase slug for an id, e.g. "Spa & Wellness" -> "spa_wellness". */
export function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Slug of *label*, with a _2, _3... suffix when another type already uses it. */
export function idFromLabel(label: string, takenIds: string[]): string {
  const base = slugify(label);
  let id = base;
  for (let n = 2; id && takenIds.includes(id); n++) id = `${base}_${n}`;
  return id;
}

/** Whether a type's id should be rebuilt from its label (it was made from the label, or is unusable). */
export function idFollowsLabel(type: ReservationType, otherIds: string[]): boolean {
  const id = type.id.trim();
  const base = slugify(type.label);
  if (!id || !SLUG_RE.test(id) || otherIds.includes(id)) return true;
  return id === base || (!!base && new RegExp(`^${base}_\\d+$`).test(id));
}

/** Placeholders a URL template uses, in order of first use. */
export function templatePlaceholders(template: string | undefined): string[] {
  return [...new Set([...(template || "").matchAll(/\{(\w+)\}/g)].map((match) => match[1]))];
}

/** Placeholders a link-out template can use: the built-in fields and the type's custom fields. */
export function availablePlaceholders(type: ReservationType): string[] {
  return [...RESERVATION_FIELDS, ...customFields(type).map((field) => field.id).filter(Boolean)];
}

export function fieldLabel(field: ReservationFieldSetting): string {
  if (!field.custom && isBuiltinField(field.id)) return RESERVATION_FIELD_LABELS[field.id];
  return (field.label || "").trim() || field.id;
}

/** Collected fields a link-out template never passes on. */
export function unusedFields(type: ReservationType): ReservationFieldSetting[] {
  if (type.mode !== "link_out") return [];
  const used = templatePlaceholders(type.url_template);
  return type.fields.filter((field) => !used.includes(field.id));
}

/**
 * The contact field the chatbot asks for anyway on an enquiry where neither
 * email nor phone is required (the first one collected), else ``null``.
 */
export function impliedContactField(type: ReservationType): ReservationFieldSetting | null {
  if (type.mode !== "enquiry") return null;
  const contacts = type.fields.filter((field) => !field.custom && CONTACT_FIELDS.includes(field.id as ReservationField));
  return contacts.length && !contacts.some((field) => field.required) ? contacts[0] : null;
}

export type ReservationPresetKey = "table" | "appointment" | "spa" | "event" | "tour" | "custom";

interface ReservationPreset {
  key: ReservationPresetKey;
  name: string;
  id: string;
  label: string;
  mode: ReservationMode;
  fields: ReservationFieldSetting[];
}

const f = builtinField;
export const RESERVATION_PRESETS: ReservationPreset[] = [
  { key: "table", name: "Table", id: "table", label: "Book a table", mode: "link_out", fields: [f("date"), f("time"), f("party_size")] },
  {
    key: "appointment", name: "Appointment", id: "appointment", label: "Book an appointment", mode: "enquiry",
    fields: [f("date"), f("time"), f("name"), f("email", false), f("phone"), f("notes")],
  },
  {
    key: "spa", name: "Spa treatment", id: "spa_treatment", label: "Book a spa treatment", mode: "enquiry",
    fields: [f("date"), f("time"), f("name"), f("phone"), f("notes")],
  },
  { key: "event", name: "Event", id: "event", label: "Book an event", mode: "enquiry", fields: [f("date"), f("party_size"), f("name"), f("email")] },
  { key: "tour", name: "Tour", id: "tour", label: "Book a tour", mode: "enquiry", fields: [f("date"), f("party_size"), f("name"), f("email")] },
  {
    key: "custom", name: "Custom", id: "", label: "", mode: "enquiry",
    fields: [f("date"), f("time"), f("party_size"), f("name"), f("email"), f("phone")],
  },
];

const SUGGESTED_PRESETS: Partial<Record<BusinessType, ReservationPresetKey>> = {
  restaurant: "table",
  clinic: "appointment",
  spa_salon: "spa",
  events_venue: "event",
};

export function suggestedPreset(businessType: BusinessType): ReservationPresetKey {
  return SUGGESTED_PRESETS[businessType] || "custom";
}

/** A new type from a preset, with an id not used by *existing* yet. */
export function newReservationType(
  preset: ReservationPresetKey = "custom",
  enquiryEmail = "",
  existing: ReservationType[] = [],
): ReservationType {
  const template = RESERVATION_PRESETS.find((item) => item.key === preset) || RESERVATION_PRESETS[RESERVATION_PRESETS.length - 1];
  const ids = existing.map((type) => type.id.trim());
  let id = template.id;
  for (let n = 2; id && ids.includes(id); n++) id = `${template.id}_${n}`;
  return {
    id,
    label: template.label,
    mode: template.mode,
    date_format: DEFAULT_DATE_FORMAT,
    fields: template.fields.map((field) => ({ ...field })),
    url_template: "",
    enquiry_email: template.mode === "enquiry" ? enquiryEmail : "",
  };
}

function parseField(raw: unknown): ReservationFieldSetting | null {
  const entry = typeof raw === "string" ? { id: raw } : raw;
  if (!entry || typeof entry !== "object") return null;
  const item = entry as Record<string, unknown>;
  const id = String(item.id ?? "").trim();
  const required = typeof item.required === "boolean" ? item.required : null;
  if (isBuiltinField(id)) return builtinField(id, required ?? undefined);
  if (typeof raw === "string" || !id) return null;
  return { id, label: String(item.label ?? ""), type: "text", required: required ?? true, custom: true };
}

/** The saved list (snake_case or camelCase, or a JSON string) as editable reservation types. */
export function parseReservationTypes(raw: unknown): ReservationType[] {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  return value
    .filter((item) => item && typeof item === "object")
    .map((item: any) => ({
      id: String(item.id ?? ""),
      label: String(item.label ?? ""),
      mode: item.mode === "link_out" ? "link_out" : "enquiry",
      url_template: String(item.url_template ?? item.urlTemplate ?? ""),
      date_format: String(item.date_format ?? item.dateFormat ?? "") || DEFAULT_DATE_FORMAT,
      fields: (Array.isArray(item.fields) ? item.fields : [])
        .map(parseField)
        .filter((field: ReservationFieldSetting | null): field is ReservationFieldSetting => field !== null)
        .filter((field: ReservationFieldSetting, index: number, all: ReservationFieldSetting[]) =>
          all.findIndex((other) => other.id === field.id) === index),
      enquiry_email: String(item.enquiry_email ?? item.enquiryEmail ?? ""),
    }));
}

/** Why the custom fields can't be saved as the chatbot reads them, or ``undefined``. */
function customFieldError(type: ReservationType): string | undefined {
  const custom = customFields(type);
  if (custom.length > MAX_CUSTOM_FIELDS) return `Add at most ${MAX_CUSTOM_FIELDS} extra details.`;
  const ids = type.fields.map((field) => field.id);
  for (const field of custom) {
    const label = (field.label || "").trim();
    if (!label) return "Every extra detail needs a name.";
    if (label.length > MAX_FIELD_LABEL) return `"${label.slice(0, 20)}…" is too long (at most ${MAX_FIELD_LABEL} characters).`;
    if (!SLUG_RE.test(field.id)) return `Use letters or numbers in "${label}".`;
    if (isBuiltinField(field.id)) return `"${label}" is already a standard detail; tick it instead.`;
    if (RESERVED_FIELD_IDS.includes(field.id)) return `"${label}" can't be used as a name; pick another.`;
    if (ids.indexOf(field.id) !== ids.lastIndexOf(field.id)) return `"${label}" is used more than once.`;
  }
  return undefined;
}

/** Validation errors per reservation type, in list order (an empty object when valid). */
export function reservationTypeErrors(types: ReservationType[]): ReservationTypeErrors[] {
  const ids = types.map((type) => type.id.trim());
  return types.map((type, index) => {
    const errors: ReservationTypeErrors = {};
    const id = ids[index];
    if (!id) errors.id = "ID is required!";
    else if (!SLUG_RE.test(id)) errors.id = "Use lowercase letters, numbers, - or _ (e.g. spa_treatment).";
    else if (ids.indexOf(id) !== index) errors.id = "Another reservation type already uses this ID.";

    if (!type.label.trim()) errors.label = "Button label is required!";
    const custom = customFieldError(type);
    if (custom) errors.custom_fields = custom;

    if (type.mode === "link_out") {
      const template = (type.url_template || "").trim();
      const known = availablePlaceholders(type);
      const unknown = templatePlaceholders(template).filter((name) => !known.includes(name));
      if (!template) errors.url_template = "Booking URL is required!";
      else if (!/^https?:\/\/\S+$/i.test(template) || !isHttpUrl(template.replace(/\{\w+\}/g, "x"))) {
        errors.url_template = "Enter a valid http(s) URL.";
      } else if (unknown.length) {
        errors.url_template = `Unknown placeholder ${unknown.map((name) => `{${name}}`).join(", ")}.`;
      }
    } else {
      const email = (type.enquiry_email || "").trim();
      if (!email) errors.enquiry_email = "Enquiry email is required!";
      else if (!EMAIL_RE.test(email)) errors.enquiry_email = "Enter a valid email address!";
      if (!type.fields.some((field) => !field.custom && CONTACT_FIELDS.includes(field.id as ReservationField))) {
        errors.fields = "Ask for the guest's email or phone so you can reply.";
      }
    }
    return errors;
  });
}

export function hasReservationTypeErrors(errors: ReservationTypeErrors[]): boolean {
  return errors.some((error) => Object.keys(error).length > 0);
}

function savedField(field: ReservationFieldSetting): SavedReservationField {
  if (!field.custom && isBuiltinField(field.id)) {
    return field.required === !OPTIONAL_FIELDS.includes(field.id) ? field.id : { id: field.id, required: field.required };
  }
  return { id: field.id, label: (field.label || "").trim(), type: "text", required: field.required };
}

/**
 * The list saved to ``property_details.reservation_types``: trimmed, with only
 * the mode's own settings, built-in fields first in the standard order.
 */
export function reservationTypesPayload(types: ReservationType[]) {
  return types.map((type) => {
    const builtins = RESERVATION_FIELDS.flatMap((id) => type.fields.filter((field) => !field.custom && field.id === id));
    const base = {
      id: type.id.trim(),
      label: type.label.trim(),
      mode: type.mode,
      date_format: DATE_FORMATS.includes(type.date_format || "") ? type.date_format : DEFAULT_DATE_FORMAT,
      fields: [...builtins, ...customFields(type)].map(savedField),
    };
    return type.mode === "link_out"
      ? { ...base, url_template: (type.url_template || "").trim() }
      : { ...base, enquiry_email: (type.enquiry_email || "").trim() };
  });
}
